import React from 'react';
import orderBy from 'lodash/orderBy';
import { TiminatorTableColumn } from '../types';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
    columnId: string;
    direction: SortDirection;
}

const getDefaultSortValue = <Entity,>(row: Entity, columnId: string) => {
    if (row && typeof row === 'object') {
        const record = row as Record<string, unknown>;
        return record[columnId];
    }
    return undefined;
};

export const useTableSorting = <Entity,> (
    data: Entity[] | null,
    columns: TiminatorTableColumn<Entity>[],
    initialSort?: SortConfig
) => {
    const [sortConfig, setSortConfig] = React.useState<SortConfig>(() => {
        if (initialSort) {
            return initialSort;
        }
        // Find the first sortable column for the initial sort
        const firstSortableColumn = columns.find(col => col.sortable);
        return firstSortableColumn 
            ? { columnId: firstSortableColumn.id, direction: 'asc' as SortDirection }
            : { columnId: columns[0]?.id ?? '', direction: 'asc' as SortDirection };
    });

    const sortedData = React.useMemo(() => {
        if (!data || data.length === 0) {
            return data;
        }

        const column = columns?.find(col => col.id === sortConfig.columnId);
        if (!column || !column.sortable) {
            return data;
        }

        const iteratee = column.getSortValue
            ? column.getSortValue
            : (row: Entity) => getDefaultSortValue(row, column.id);

        return orderBy(data, [iteratee], [sortConfig.direction]);
    }, [columns, data, sortConfig]);

    React.useEffect(() => {
        const exists = columns?.some(column => column.id === sortConfig.columnId && column.sortable);
        if (!exists) {
            const firstSortableColumn = columns.find(col => col.sortable);
            if (firstSortableColumn) {
                setSortConfig({ columnId: firstSortableColumn.id, direction: 'asc' });
            }
        }
    }, [columns, sortConfig]);

    const handleSortToggle = React.useCallback((column: TiminatorTableColumn<Entity>) => {
        if (!column.sortable) {
            return;
        }

        setSortConfig(prev => {
            // if click on another column, sort by it in ascending order
            if (!prev || prev.columnId !== column.id) {
                return { columnId: column.id, direction: 'asc' };
            }
            // if it's the same column, switch the direction
            return { 
                columnId: column.id, 
                direction: prev.direction === 'asc' ? 'desc' : 'asc' 
            };
        });
    }, []);

    return { sortedData, sortConfig, handleSortToggle };
};
